import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popover from '@mui/material/Popover'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { Fragment, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthProvider'
import { isPlatformPath, PLATFORM_ROOT } from '../utils/platformMode'

interface MenuOption {
  label: string
  action?: () => void
}

export default function AccountPopover() {
  const [open, setOpen] = useState<HTMLElement | null>(null)
  const { auth } = useAuth()
  const { user } = auth 
  const navigate = useNavigate()
  const location = useLocation()

  const { platformAdmin } = auth
  const inPlatform = isPlatformPath(location.pathname)
  const displayName = user?.username ?? `${platformAdmin?.firstName ?? ''} ${platformAdmin?.lastName ?? ''}`.trim()
  const displayEmail = user?.email ?? platformAdmin?.email

  const go = (path: string, state?: unknown) => {
    handleClose()
    navigate(path, state ? { state } : undefined)
  }

  const MENU_OPTIONS: MenuOption[] = inPlatform
    ? [
        { label: 'Inicio plataforma', action: () => go(PLATFORM_ROOT) },
        // Only people who also have a client identity can switch back.
        ...(user ? [{ label: `Cambiar a ${user.client?.name ?? 'cliente'}`, action: () => go('/') }] : []),
      ]
    : [
        { label: 'Inicio', action: () => go('/') },
        {
          label: 'Perfil',
          action: () => go(`/usuarios/detalles/${user?.userid}`, { id: user?.userid, backTo: location.pathname }),
        },
        ...(platformAdmin ? [{ label: 'Cambiar a Plataforma', action: () => go(PLATFORM_ROOT) }] : []),
      ]

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget)
  }

  const handleClose = () => {
    setOpen(null)
  }

  const handleLogOut = () => {
    navigate('/logout')
  }

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        pr: 0,
      }}
    >
      <IconButton
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.imageurl}
          alt={displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              mt: 1,
              ml: 0.75,
              width: 200,
            },
          },
        }}
      >
        <Box sx={{ mt: 1.5, px: 2 }}>         
          <Typography variant='body2'>
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {inPlatform ? 'Modo:' : 'Cliente:'}
            </Typography>
            {' '}{inPlatform ? 'Plataforma' : user?.client?.name}
          </Typography>
        </Box>        
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant='subtitle2' noWrap>
            {displayName}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }} noWrap>
            {displayEmail}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuList>
          {MENU_OPTIONS.map((option) => (
            <Fragment key={option.label}>
              <MenuItem onClick={option.action}>
                {option.label}
              </MenuItem>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Fragment>
          ))}

          <MenuItem
            disableRipple
            disableTouchRipple
            onClick={handleLogOut}
            sx={{ typography: 'body2', color: 'error.main', py: 1.5 }}
          >
            Cerrar sesión
          </MenuItem>
        </MenuList>
      </Popover>
    </Container>
  )
}
